/**
 * lib/pult-layout.ts — the pult (task 54): every part of the board, where it stands and how big
 * it is, in points of the board.
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Экран, раскладка, поворот». The board is a
 * copy of a Rane System One measured off a photo (`.omc/wiki/pult-system-one.md`): the owner's
 * page «Пульт Flash System» draws it with a generator, `.omc/wiki/raw/pult-generator-3/sxlib.py`,
 * and this module is that generator's numbers, carried over call for call — `landscape()` to
 * {@link PULT_LAYOUT}`.land` (780 × 390), `portrait()` to `.port` (390 × 780). The same
 * device-unit coordinates go through the same stretch (`fx`, `fy`, `ox`, `oy`), so the order of
 * the parts is the generator's too. `test/pult-layout.test.ts` lays the two side by side against
 * a snapshot of the generator (`test/fixtures/pult-generator.json`): the app and the owner's page
 * show one board. A change of the board is a new generator first, then a new snapshot, then here.
 *
 * ## Contract with the screen (`components/PultBoard.tsx`) and the tasks after 54
 *
 * Every control the hand can work has an `id`: `A.…` and `B.…` for a deck's, bare names for the
 * middle's (`load.A`, `xfader`, `fx.echo`, …). Tasks 55–62 give those ids their sound; the ids
 * do not change when they do. Print and frames (`label`, `bar`, `box`, `panel`, `tick`) have no
 * id: they are the drawing.
 *
 * What the generator draws as a state of its demo — which of 33/45 is lit, START·STOP on or off,
 * ECHO chosen, FX A up — is the app's state, not the layout: the table draws every such key
 * unlit (`toggle`), and the board lights it from the stores.
 */

/** The two decks. */
export type DeckSide = 'A' | 'B';

/** Which deck a control belongs to, or the middle. */
export type PultSide = DeckSide | 'mid';

/** The two boards: phone on its side, and upright. */
export type PultOrientation = 'land' | 'port';

/** Points of the board, top-left corner and size. */
export interface PultRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** The faces of a key, as the generator names them (`sx.css`, `.b.<look>`). */
export type KeyLook = 'w' | 'dark' | 'sync' | 'cue' | 'ss' | 'blue' | 'violet' | 'orange' | 'red';

/** The faces of a knob: a small black one, the white EQ caps, the lit ring of the filter and FX. */
export type KnobLook = 'black' | 'white' | 'ring';

/** Print on the board: plain, grey, blue, a logo, the name of a pad mode. */
export type LabelTone = 'plain' | 'dim' | 'blue' | 'logo' | 'mode';

export type PultPart =
  | {
      readonly kind: 'label';
      /** The centre of the print. */
      readonly x: number;
      readonly y: number;
      readonly fs: number;
      readonly text: string;
      readonly tone: LabelTone;
      /** The pad mode it names, for the mode the deck is in to light it. */
      readonly id?: string | undefined;
    }
  | {
      readonly kind: 'key';
      readonly id: string;
      readonly side: PultSide;
      readonly rect: PultRect;
      readonly look: KeyLook;
      readonly text: string;
      /** PARAMETER's two keys carry a triangle, not a word. */
      readonly glyph?: 'left' | 'right' | undefined;
      readonly fs: number;
      /** Lit by a state of the app (33/45, an effect chosen, AUTO): drawn unlit here. */
      readonly toggle?: true | undefined;
    }
  | { readonly kind: 'bar'; readonly rect: PultRect; readonly text: string; readonly fs: number }
  | {
      readonly kind: 'knob';
      readonly id: string;
      readonly side: PultSide;
      readonly rect: PultRect;
      readonly look: KnobLook;
    }
  | {
      readonly kind: 'fader';
      readonly id: string;
      readonly side: PultSide;
      readonly axis: 'v' | 'h';
      /** The slot the cap runs in. */
      readonly slot: PultRect;
      /** The cap's size; where it stands along the slot is the app's state. */
      readonly capW: number;
      readonly capH: number;
      /** The scale beside a vertical fader. */
      readonly ticks: PultRect | undefined;
    }
  | {
      readonly kind: 'platter';
      readonly id: string;
      readonly side: PultSide;
      readonly rect: PultRect;
    }
  | {
      readonly kind: 'pad';
      readonly id: string;
      readonly side: PultSide;
      readonly rect: PultRect;
      /** 0–7, the colour of the pad from {@link PAD_COLOURS}. */
      readonly colour: number;
    }
  | {
      readonly kind: 'oled';
      readonly id: string;
      readonly side: PultSide;
      readonly rect: PultRect;
      readonly fs: number;
    }
  | { readonly kind: 'box'; readonly rect: PultRect; readonly tone: 'plain' | 'blue' }
  | { readonly kind: 'panel'; readonly rect: PultRect }
  | { readonly kind: 'tick'; readonly rect: PultRect }
  | {
      readonly kind: 'paddle';
      readonly id: string;
      readonly side: PultSide;
      readonly rect: PultRect;
    }
  | { readonly kind: 'screen'; readonly rect: PultRect }
  | {
      readonly kind: 'meter';
      readonly rect: PultRect;
      /** Lights in each of MAIN's four columns: 13 on its side, 24 upright, as the generator's. */
      readonly segments: number;
    };

export interface PultBoardLayout {
  readonly orientation: PultOrientation;
  readonly width: number;
  readonly height: number;
  readonly parts: readonly PultPart[];
}

/** The pads' colours, as the generator's `PADS`: face, highlight, shade, glow. */
export const PAD_COLOURS: readonly (readonly [string, string, string, string])[] = Object.freeze([
  ['#ff3b30', '#ffa198', '#b3140c', 'rgba(255,59,48,.7)'],
  ['#ff8a3c', '#ffc996', '#c2500e', 'rgba(255,138,60,.65)'],
  ['#6a6cff', '#b9baff', '#3a33c9', 'rgba(106,108,255,.7)'],
  ['#ffe14a', '#fff4ad', '#c4a10c', 'rgba(255,225,74,.55)'],
  ['#3fd65a', '#acf6b7', '#14902a', 'rgba(63,214,90,.65)'],
  ['#ff5fd0', '#ffb8ec', '#bd2296', 'rgba(255,95,208,.65)'],
  ['#33dbe0', '#aaf6f8', '#0d979b', 'rgba(51,219,224,.65)'],
  ['#a374ff', '#d8c2ff', '#6936d4', 'rgba(163,116,255,.65)'],
]);

/** The four pad modes, left to right, with the ids their keys and print carry. */
export const PAD_MODES = Object.freeze([
  ['hotcue', 'HOT CUE'],
  ['roll', 'ROLL'],
  ['sampler', 'SAMPLER'],
  ['loop', 'LOOP'],
] as const);

/** Task 60: a pad mode, as its key's id ends: `A.mode.hotcue`. */
export type PadMode = (typeof PAD_MODES)[number][0];

/** The effect keys over the paddles, left to right. */
const FX_KEYS = Object.freeze([
  ['echo', 'ECHO'],
  ['reverb', 'REVERB'],
  ['flanger', 'FLANGER'],
  ['phaser', 'PHASER'],
] as const);

/** The screen's three views, as the keys between the two LOADs name them. */
export const SCREEN_VIEWS = Object.freeze([
  ['queue', 'QUEUE'],
  ['library', 'LIBRARY'],
  ['view', 'VIEW'],
] as const);

/** Two decimals, as the generator writes them into its page. */
function r2(value: number): number {
  return Math.round(value * 100) / 100 + 0;
}

/**
 * The generator's `C`: device units to board points. `fx`/`fy` stretch the photo's measure onto
 * the board, `ox`/`oy` move it off the rounded frame; sizes of round things go by `fy` alone.
 */
class Builder {
  readonly parts: PultPart[] = [];

  constructor(
    private readonly fx: number,
    private readonly fy: number,
    private readonly ox: number,
    private readonly oy: number,
  ) {}

  private X(x: number): number {
    return x * this.fx + this.ox;
  }

  private Y(y: number): number {
    return y * this.fy + this.oy;
  }

  private S(v: number): number {
    return v * this.fy;
  }

  private rect(x1: number, y1: number, x2: number, y2: number): PultRect {
    const X1 = this.X(x1);
    const Y1 = this.Y(y1);
    return { x: r2(X1), y: r2(Y1), w: r2(this.X(x2) - X1), h: r2(this.Y(y2) - Y1) };
  }

  t(
    cx: number,
    cy: number,
    text: string,
    tone: LabelTone = 'plain',
    size = 3.4,
    id?: string,
  ): void {
    this.parts.push({
      kind: 'label',
      x: r2(this.X(cx)),
      y: r2(this.Y(cy)),
      fs: r2(this.S(size)),
      text,
      tone,
      id,
    });
  }

  btn(
    id: string,
    side: PultSide,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    look: KeyLook = 'w',
    text = '',
    size = 3.2,
    extra: { readonly glyph?: 'left' | 'right'; readonly toggle?: true } = {},
  ): void {
    this.parts.push({
      kind: 'key',
      id,
      side,
      rect: this.rect(x1, y1, x2, y2),
      look,
      text,
      fs: r2(this.S(size)),
      ...extra,
    });
  }

  bar(x1: number, y1: number, x2: number, y2: number, text: string, size = 2.6): void {
    this.parts.push({ kind: 'bar', rect: this.rect(x1, y1, x2, y2), text, fs: r2(this.S(size)) });
  }

  knob(
    id: string,
    side: PultSide,
    cx: number,
    cy: number,
    d: number,
    look: KnobLook = 'black',
  ): void {
    const D = this.S(d);
    this.parts.push({
      kind: 'knob',
      id,
      side,
      rect: { x: r2(this.X(cx) - D / 2), y: r2(this.Y(cy) - D / 2), w: r2(D), h: r2(D) },
      look,
    });
  }

  vfader(
    id: string,
    side: PultSide,
    cx: number,
    y1: number,
    y2: number,
    capw: number,
    caph: number,
    slotw = 3.2,
    ticks: 'l' | 'r' = 'l',
  ): void {
    const X = this.X(cx);
    const Y1 = this.Y(y1);
    const Y2 = this.Y(y2);
    const CW = this.S(capw);
    const tx = ticks === 'l' ? X - CW / 2 - this.S(3.2) : X + CW / 2 + this.S(1.2);
    const SW = this.S(slotw);
    this.parts.push({
      kind: 'fader',
      id,
      side,
      axis: 'v',
      slot: { x: r2(X - SW / 2), y: r2(Y1), w: r2(SW), h: r2(Y2 - Y1) },
      capW: r2(CW),
      capH: r2(this.S(caph)),
      ticks: { x: r2(tx), y: r2(Y1), w: r2(this.S(2)), h: r2(Y2 - Y1) },
    });
  }

  hfader(
    id: string,
    x1: number,
    x2: number,
    cy: number,
    capw: number,
    caph: number,
    sloth = 3.2,
  ): void {
    const X1 = this.X(x1);
    const Y = this.Y(cy);
    const SH = this.S(sloth);
    this.parts.push({
      kind: 'fader',
      id,
      side: 'mid',
      axis: 'h',
      slot: { x: r2(X1), y: r2(Y - SH / 2), w: r2(this.X(x2) - X1), h: r2(SH) },
      capW: r2(this.S(capw)),
      capH: r2(this.S(caph)),
      ticks: undefined,
    });
  }

  platter(id: string, side: PultSide, cx: number, cy: number, d: number): void {
    const D = this.S(d);
    this.parts.push({
      kind: 'platter',
      id,
      side,
      rect: { x: r2(this.X(cx) - D / 2), y: r2(this.Y(cy) - D / 2), w: r2(D), h: r2(D) },
    });
  }

  pad(
    id: string,
    side: PultSide,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    colour: number,
  ): void {
    this.parts.push({ kind: 'pad', id, side, rect: this.rect(x1, y1, x2, y2), colour });
  }

  oled(
    id: string,
    side: PultSide,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    size = 2.5,
  ): void {
    this.parts.push({
      kind: 'oled',
      id,
      side,
      rect: this.rect(x1, y1, x2, y2),
      fs: r2(this.S(size)),
    });
  }

  box(x1: number, y1: number, x2: number, y2: number, tone: 'plain' | 'blue' = 'plain'): void {
    this.parts.push({ kind: 'box', rect: this.rect(x1, y1, x2, y2), tone });
  }

  panel(x1: number, y1: number, x2: number, y2: number): void {
    this.parts.push({ kind: 'panel', rect: this.rect(x1, y1, x2, y2) });
  }

  tick(x1: number, y1: number, x2: number, y2: number): void {
    this.parts.push({ kind: 'tick', rect: this.rect(x1, y1, x2, y2) });
  }

  paddle(id: string, side: PultSide, x1: number, y1: number, x2: number, y2: number): void {
    this.parts.push({ kind: 'paddle', id, side, rect: this.rect(x1, y1, x2, y2) });
  }

  screen(x1: number, y1: number, x2: number, y2: number): void {
    this.parts.push({ kind: 'screen', rect: this.rect(x1, y1, x2, y2) });
  }

  meter(x1: number, y1: number, x2: number, y2: number, segments: number): void {
    this.parts.push({ kind: 'meter', rect: this.rect(x1, y1, x2, y2), segments });
  }
}

/* ── phone on its side: `landscape()`, 780 × 390 ───────────────────────────── */

/** `l_deck`: one deck of the board on its side, `o` units right of deck A. */
function landDeck(c: Builder, d: DeckSide, o: number, logo: string, logoSize: number): void {
  const id = (name: string): string => `${d}.${name}`;
  // top row
  c.t(45 + o, 13.5, '◂ BEAT JUMP ▸', 'plain', 3.2);
  c.btn(id('jump.back'), d, 27 + o, 18, 40 + o, 29);
  c.btn(id('jump.fwd'), d, 51 + o, 18, 64 + o, 29);
  c.t(80 + o, 12, 'JUMP SIZE', 'plain', 2.9);
  c.knob(id('jumpsize'), d, 80 + o, 23, 12);
  c.t(80 + o, 33.5, '◂ ½  2× ▸', 'dim', 2.6);
  c.t(128 + o, 21, logo, 'logo', logoSize);
  c.btn(id('slip'), d, 165 + o, 17, 177 + o, 29, 'blue');
  c.t(171 + o, 33.5, 'SLIP', 'plain', 3);
  c.t(195 + o, 12.5, 'CENSOR', 'plain', 3);
  c.btn(id('censor'), d, 189 + o, 17, 201 + o, 29, 'red');
  c.t(195 + o, 33.5, 'REVERSE', 'dim', 2.7);
  c.t(248 + o, 11.5, 'KEYLOCK', 'plain', 2.7);
  c.btn(id('keylock'), d, 240 + o, 15.5, 256 + o, 25.5, 'blue');
  c.t(248 + o, 30.5, '± 8 %', 'dim', 2.7);
  // platter
  c.tick(126.3 + o, 37, 127.7 + o, 44);
  c.platter(id('platter'), d, 127 + o, 152, 210);
  // speed box: 33 and 45 change the record, as on the turntable
  c.box(236 + o, 38, 258 + o, 97);
  c.t(247 + o, 43, 'SPEED', 'plain', 2.9);
  c.btn(id('rpm33'), d, 240 + o, 47, 254 + o, 56, 'w', '33', 3.2, { toggle: true });
  c.btn(id('rpm45'), d, 240 + o, 61, 254 + o, 70, 'w', '45', 3.2, { toggle: true });
  c.btn(id('reset'), d, 240 + o, 75, 254 + o, 84, 'w', '0 %', 2.8);
  c.t(247 + o, 90.5, 'RESET', 'dim', 2.5);
  // pitch
  c.t(259 + o, 107, '−', 'plain', 4);
  c.t(259 + o, 254, '+', 'plain', 4);
  c.vfader(id('pitch'), d, 247 + o, 104, 258, 22, 11, 3.6, 'l');
  // transport
  c.btn(id('sync'), d, 17 + o, 272, 51 + o, 290, 'sync', 'SYNC', 3.8);
  c.btn(id('cue'), d, 17 + o, 296, 51 + o, 314, 'cue', 'CUE', 3.8);
  c.btn(id('play'), d, 17 + o, 321, 51 + o, 356, 'ss', 'START·STOP', 3.4, { toggle: true });
  // pad modes
  const slots = [
    [62, 90],
    [94, 122],
    [127, 154],
    [159, 186],
  ] as const;
  PAD_MODES.forEach(([mode, name], i) => {
    const [x1, x2] = slots[i] ?? [0, 0];
    c.t((x1 + x2) / 2 + o, 268.5, name, 'mode', 2.8, id(`mode.${mode}`));
    c.btn(id(`mode.${mode}`), d, x1 + o, 273, x2 + o, 283, 'w', '', 3.2, { toggle: true });
  });
  c.oled(id('oled'), d, 60 + o, 288, 189 + o, 304);
  const xs = [
    [62, 90],
    [94, 122],
    [126, 154],
    [159, 187],
  ] as const;
  [
    [309, 330],
    [335, 356],
  ].forEach(([y1 = 0, y2 = 0], r) => {
    xs.forEach(([x1, x2], i) => {
      c.pad(id(`pad${String(r * 4 + i + 1)}`), d, x1 + o, y1, x2 + o, y2, r * 4 + i);
    });
  });
  // right column
  c.t(209 + o, 270.5, '◂ PARAMETER ▸', 'plain', 2.6);
  c.btn(id('param.prev'), d, 194 + o, 275, 206 + o, 303, 'w', '', 6.5, { glyph: 'left' });
  c.btn(id('param.next'), d, 212 + o, 275, 224 + o, 303, 'w', '', 6.5, { glyph: 'right' });
  c.t(249 + o, 270.5, '◂ PITCH BEND ▸', 'plain', 2.6);
  c.btn(id('bend.down'), d, 234 + o, 275, 246 + o, 303, 'w', '−', 6.5);
  c.btn(id('bend.up'), d, 252 + o, 275, 264 + o, 303, 'w', '+', 6.5);
  c.box(191 + o, 311, 227 + o, 357);
  c.t(209 + o, 315.5, 'LOOP ON/OFF', 'plain', 2.6);
  c.btn(id('loop'), d, 194 + o, 319, 225 + o, 328, 'orange');
  c.bar(196 + o, 330.5, 223 + o, 333.5, 'RELOOP');
  c.btn(id('loop.half'), d, 194 + o, 340, 206 + o, 349, 'orange', '½', 3.4);
  c.btn(id('loop.double'), d, 212 + o, 340, 224 + o, 349, 'orange', '2×', 3.2);
  c.bar(194 + o, 351.5, 206 + o, 354.5, 'IN');
  c.bar(212 + o, 351.5, 224 + o, 354.5, 'OUT');
  c.box(230 + o, 311, 268 + o, 357, 'blue');
  c.t(249 + o, 315.5, 'SELECTA', 'blue', 2.8);
  c.btn(id('pullup'), d, 233 + o, 319, 265 + o, 328, 'violet');
  c.bar(235 + o, 330.5, 263 + o, 333.5, 'PULL UP');
  c.btn(id('echoout'), d, 233 + o, 340, 265 + o, 349, 'violet');
  c.bar(235 + o, 351.5, 263 + o, 354.5, 'ECHO OUT');
}

/** `l_center`: the middle of the board on its side. */
function landCentre(c: Builder): void {
  c.btn('load.A', 'mid', 277, 6, 305, 16, 'blue', '◂ LOAD', 2.9);
  for (const [[view, name], x1, x2] of [
    [SCREEN_VIEWS[0], 313, 340],
    [SCREEN_VIEWS[1], 349, 381],
    [SCREEN_VIEWS[2], 390, 417],
  ] as const) {
    c.btn(`screen.${view}`, 'mid', x1, 6, x2, 16, 'w', name, 2.7, { toggle: true });
  }
  c.btn('load.B', 'mid', 426, 6, 454, 16, 'blue', 'LOAD ▸', 2.9);
  for (const [cx, d] of [
    [291, 'A'],
    [437, 'B'],
  ] as const) {
    c.t(cx, 22.5, 'LEVEL', 'plain', 2.6);
    c.knob(`${d}.level`, d, cx, 32, 11);
    for (const [y, name, key] of [
      [58, 'HIGH', 'high'],
      [88, 'MID', 'mid'],
      [118, 'LOW', 'low'],
    ] as const) {
      c.knob(`${d}.${key}`, d, cx, y, 18, 'white');
      c.t(cx, y + 12.5, name, 'plain', 2.6);
    }
    c.knob(`${d}.filter`, d, cx, 160, 25, 'ring');
    c.t(cx, 177, 'FILTER', 'plain', 2.7);
  }
  c.screen(310, 20, 421, 190);
  FX_KEYS.forEach(([fx, name], i) => {
    const x1 = 314 + i * 26.5;
    c.btn(`fx.${fx}`, 'mid', x1, 196, x1 + 23, 203, 'dark', '', 3.2, { toggle: true });
    c.t(x1 + 11.5, 207.5, name, 'plain', 2.6);
  });
  c.paddle('A.fx', 'A', 276, 213, 308, 246);
  c.paddle('B.fx', 'B', 423, 213, 455, 246);
  c.t(292, 249.5, 'FX A', 'plain', 2.6);
  c.t(439, 249.5, 'FX B', 'plain', 2.6);
  c.knob('fx.beats', 'mid', 346, 227, 19, 'ring');
  c.t(346, 241.5, 'BEATS', 'plain', 2.7);
  c.knob('fx.depth', 'mid', 385, 227, 19, 'ring');
  c.t(385, 241.5, 'DEPTH', 'plain', 2.7);
  // mixer
  c.panel(275, 253, 457, 374);
  c.btn('auto', 'mid', 281, 262, 299, 271, 'blue', 'AUTO', 2.8, { toggle: true });
  c.t(290, 274.5, 'АВТОМИКС', 'dim', 2.3);
  c.btn('preset', 'mid', 433, 262, 451, 271, 'dark', 'PRESET', 2.4);
  c.t(442, 274.5, 'DUB OUT', 'blue', 2.3);
  c.t(365.5, 262, 'MAIN', 'plain', 2.6);
  c.vfader('A.fader', 'A', 317, 271, 323, 19, 9.5, 3, 'l');
  c.vfader('B.fader', 'B', 413, 271, 323, 19, 9.5, 3, 'r');
  c.meter(349, 270, 382, 318, 13);
  c.t(317, 329, '1', 'plain', 3.4);
  c.t(413, 329, '2', 'plain', 3.4);
  c.t(326, 350, 'A', 'plain', 3.2);
  c.t(405, 350, 'B', 'plain', 3.2);
  c.hfader('xfader', 333, 398, 350, 9, 19, 3);
  c.t(298, 364, 'CROSSFADER', 'dim', 2.5);
  c.t(431, 364, 'FLASH SYSTEM', 'logo', 3.3);
}

function landscape(): PultBoardLayout {
  const c = new Builder(1.0655, 1.0163, -2.1, 1.9);
  landDeck(c, 'A', 0, 'FLASH', 9.5);
  landDeck(c, 'B', 451, 'DANCEHALL', 6.6);
  landCentre(c);
  return { orientation: 'land', width: 780, height: 390, parts: c.parts };
}

/* ── phone upright: `portrait()`, 390 × 780 ────────────────────────────────── */

/** `p_deck`: one deck as a band across the board, `y0` units down. */
function portDeck(c: Builder, d: DeckSide, y0: number, logo: string, logoSize: number): void {
  const id = (name: string): string => `${d}.${name}`;
  // strip above the platter
  c.t(44, y0 + 4.5, '◂ BEAT JUMP ▸', 'plain', 3);
  c.btn(id('jump.back'), d, 31, y0 + 8, 42, y0 + 18);
  c.btn(id('jump.fwd'), d, 47, y0 + 8, 58, y0 + 18);
  c.t(73, y0 + 4, 'SIZE', 'plain', 2.8);
  c.knob(id('jumpsize'), d, 73, y0 + 13.5, 11);
  c.t(112, y0 + 12.5, logo, 'logo', logoSize);
  c.btn(id('slip'), d, 148, y0 + 8, 159, y0 + 18, 'blue');
  c.t(153.5, y0 + 22, 'SLIP', 'plain', 2.8);
  c.t(171.5, y0 + 4, 'CENSOR', 'plain', 2.8);
  c.btn(id('censor'), d, 166, y0 + 8, 177, y0 + 18, 'red');
  c.t(205, y0 + 5, 'KEYLOCK', 'plain', 2.5);
  c.btn(id('keylock'), d, 196, y0 + 9, 214, y0 + 18, 'blue');
  c.t(205, y0 + 23, '± 8 %', 'dim', 2.6);
  c.tick(109.3, y0 + 23, 110.7, y0 + 29);
  c.platter(id('platter'), d, 110, y0 + 137, 212);
  // speed / pitch
  c.box(221, y0 + 2, 247, y0 + 61);
  c.t(234, y0 + 7, 'SPEED', 'plain', 2.7);
  c.btn(id('rpm33'), d, 225, y0 + 11, 243, y0 + 21, 'w', '33', 3.2, { toggle: true });
  c.btn(id('rpm45'), d, 225, y0 + 25, 243, y0 + 35, 'w', '45', 3.2, { toggle: true });
  c.btn(id('reset'), d, 225, y0 + 39, 243, y0 + 49, 'w', '0 %', 2.8);
  c.t(234, y0 + 55, 'RESET', 'dim', 2.4);
  c.t(245, y0 + 70, '−', 'plain', 4);
  c.t(245, y0 + 262, '+', 'plain', 4);
  c.vfader(id('pitch'), d, 232, y0 + 68, y0 + 264, 24, 12, 3.6, 'l');
  // control block x 252..388
  c.t(285, y0 + 4.5, '◂ PARAMETER ▸', 'plain', 2.5);
  c.btn(id('param.prev'), d, 256, y0 + 8, 283, y0 + 22.5, 'w', '', 7, { glyph: 'left' });
  c.btn(id('param.next'), d, 287, y0 + 8, 314, y0 + 22.5, 'w', '', 7, { glyph: 'right' });
  c.t(355, y0 + 4.5, '◂ PITCH BEND ▸', 'plain', 2.5);
  c.btn(id('bend.down'), d, 326, y0 + 8, 353, y0 + 22.5, 'w', '−', 7);
  c.btn(id('bend.up'), d, 357, y0 + 8, 384, y0 + 22.5, 'w', '+', 7);
  const xs = [
    [252, 283],
    [287, 318],
    [322, 353],
    [357, 388],
  ] as const;
  PAD_MODES.forEach(([mode, name], i) => {
    const [x1, x2] = xs[i] ?? [0, 0];
    c.t((x1 + x2) / 2, y0 + 30, name, 'mode', 2.7, id(`mode.${mode}`));
    c.btn(id(`mode.${mode}`), d, x1, y0 + 34, x2, y0 + 45, 'w', '', 3.2, { toggle: true });
  });
  c.oled(id('oled'), d, 252, y0 + 49, 388, y0 + 66, 2.7);
  [
    [y0 + 70, y0 + 106],
    [y0 + 110, y0 + 146],
  ].forEach(([y1 = 0, y2 = 0], r) => {
    xs.forEach(([x1, x2], i) => {
      c.pad(id(`pad${String(r * 4 + i + 1)}`), d, x1, y1, x2, y2, r * 4 + i);
    });
  });
  // transport
  c.btn(id('sync'), d, 252, y0 + 151, 296, y0 + 174, 'sync', 'SYNC', 4.4);
  c.btn(id('cue'), d, 252, y0 + 181, 296, y0 + 204, 'cue', 'CUE', 4.4);
  c.btn(id('play'), d, 252, y0 + 210, 296, y0 + 264, 'ss', 'START·STOP', 4, { toggle: true });
  // loop box
  c.box(300, y0 + 151, 342, y0 + 264);
  c.t(321, y0 + 157, 'LOOP ON/OFF', 'plain', 2.6);
  c.btn(id('loop'), d, 304, y0 + 163, 338, y0 + 191, 'orange');
  c.bar(306, y0 + 194, 336, y0 + 198, 'RELOOP', 2.6);
  c.btn(id('loop.half'), d, 304, y0 + 206, 319, y0 + 248, 'orange', '½', 4.4);
  c.btn(id('loop.double'), d, 323, y0 + 206, 338, y0 + 248, 'orange', '2×', 4);
  c.bar(304, y0 + 252, 319, y0 + 256, 'IN', 2.5);
  c.bar(323, y0 + 252, 338, y0 + 256, 'OUT', 2.5);
  // SELECTA box
  c.box(346, y0 + 151, 388, y0 + 264, 'blue');
  c.t(367, y0 + 157, 'SELECTA', 'blue', 2.7);
  c.btn(id('pullup'), d, 350, y0 + 163, 384, y0 + 199, 'violet');
  c.bar(352, y0 + 202, 382, y0 + 206, 'PULL UP', 2.5);
  c.btn(id('echoout'), d, 350, y0 + 213, 384, y0 + 249, 'violet');
  c.bar(352, y0 + 252, 382, y0 + 256, 'ECHO OUT', 2.5);
}

/** `p_middle`: the screen block and the mixer, between the two decks. */
function portMiddle(c: Builder, m0: number): void {
  c.btn('load.A', 'mid', 3, m0 + 3, 33, m0 + 15, 'blue', '◂ LOAD', 2.9);
  for (const [[view, name], x1, x2] of [
    [SCREEN_VIEWS[0], 37, 75],
    [SCREEN_VIEWS[1], 79, 127],
    [SCREEN_VIEWS[2], 131, 169],
  ] as const) {
    c.btn(`screen.${view}`, 'mid', x1, m0 + 3, x2, m0 + 15, 'w', name, 2.8, { toggle: true });
  }
  c.btn('load.B', 'mid', 173, m0 + 3, 203, m0 + 15, 'blue', 'LOAD ▸', 2.9);
  for (const [cx, d] of [
    [17, 'A'],
    [189, 'B'],
  ] as const) {
    c.t(cx, m0 + 21.5, 'LEVEL', 'plain', 2.5);
    c.knob(`${d}.level`, d, cx, m0 + 30, 12);
    for (const [y, name, key] of [
      [m0 + 54, 'HIGH', 'high'],
      [m0 + 83, 'MID', 'mid'],
      [m0 + 112, 'LOW', 'low'],
    ] as const) {
      c.knob(`${d}.${key}`, d, cx, y, 19, 'white');
      c.t(cx, y + 13, name, 'plain', 2.5);
    }
    c.knob(`${d}.filter`, d, cx, m0 + 150, 26, 'ring');
    c.t(cx, m0 + 168, 'FILTER', 'plain', 2.6);
  }
  c.screen(34, m0 + 19, 172, m0 + 189);
  FX_KEYS.forEach(([fx, name], i) => {
    const x1 = 36 + i * 34;
    c.btn(`fx.${fx}`, 'mid', x1, m0 + 194, x1 + 30, m0 + 202, 'dark', '', 3.2, { toggle: true });
    c.t(x1 + 15, m0 + 206.5, name, 'plain', 2.6);
  });
  c.t(18, m0 + 189, 'FX A', 'plain', 2.6);
  c.paddle('A.fx', 'A', 3, m0 + 193, 33, m0 + 241);
  c.t(188, m0 + 189, 'FX B', 'plain', 2.6);
  c.paddle('B.fx', 'B', 173, m0 + 193, 203, m0 + 241);
  c.knob('fx.beats', 'mid', 80, m0 + 222, 23, 'ring');
  c.t(80, m0 + 238.5, 'BEATS', 'plain', 2.7);
  c.knob('fx.depth', 'mid', 126, m0 + 222, 23, 'ring');
  c.t(126, m0 + 238.5, 'DEPTH', 'plain', 2.7);
  // mixer block x 210..388
  c.panel(209, m0 + 3, 388, m0 + 242);
  c.btn('auto', 'mid', 216, m0 + 10, 242, m0 + 21, 'blue', 'AUTO', 3.2, { toggle: true });
  c.t(229, m0 + 25, 'АВТОМИКС', 'dim', 2.4);
  c.btn('preset', 'mid', 355, m0 + 10, 381, m0 + 21, 'dark', 'PRESET', 2.8);
  c.t(368, m0 + 25, 'DUB OUT', 'blue', 2.4);
  c.t(299, m0 + 17, 'MAIN', 'plain', 2.7);
  c.vfader('A.fader', 'A', 254, m0 + 34, m0 + 190, 24, 12, 3.4, 'l');
  c.vfader('B.fader', 'B', 344, m0 + 34, m0 + 190, 24, 12, 3.4, 'r');
  c.meter(284, m0 + 34, 314, m0 + 186, 24);
  c.t(254, m0 + 197, '1', 'plain', 3.6);
  c.t(344, m0 + 197, '2', 'plain', 3.6);
  c.t(240, m0 + 219, 'A', 'plain', 3.4);
  c.t(358, m0 + 219, 'B', 'plain', 3.4);
  c.hfader('xfader', 250, 348, m0 + 219, 13, 26, 3.4);
  c.t(228, m0 + 235, 'CROSSFADER', 'dim', 2.4);
  c.t(366, m0 + 235, 'FLASH SYSTEM', 'logo', 3);
}

function portrait(): PultBoardLayout {
  // content spans x 3..388 and y 0.5..776; mapped onto 6..384 and 6..774, off the rounded frame
  const c = new Builder(0.9818, 0.9903, 3.05, 5.5);
  portDeck(c, 'A', 0, 'FLASH', 9.5);
  portMiddle(c, 268);
  portDeck(c, 'B', 512, 'DANCEHALL', 6.6);
  return { orientation: 'port', width: 390, height: 780, parts: c.parts };
}

/** The two boards, built once. */
export const PULT_LAYOUT: Readonly<Record<PultOrientation, PultBoardLayout>> = Object.freeze({
  land: landscape(),
  port: portrait(),
});
